import React, { useState, useEffect } from 'react';
import { TimesheetStatus, ProjectType } from '../types/timesheet.types';
import type { ISODateString, PeriodMonth, BankHoliday } from '../types/timesheet.types';
import type { PendingApprovalWithWeeks, WeekSummary } from '../types/approval.types';
import type { UseManagerApprovalsResult } from '../hooks/useManagerApprovals';
import { getWeekDays } from '../utils/approvalTransform';
import { fmtHours } from '../utils/calculations';
import { useConnector } from '../contexts/ConnectorContext';
import { SP_LISTS, BANK_HOLIDAY_PROJECT_CODE, VALIDATION } from '../utils/constants';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
}

function formatPeriodMonthLabel(pm: PeriodMonth): string {
  const [year, month] = pm.split('-');
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

function formatShortDate(iso: ISODateString): string {
  const d = new Date(iso + 'T00:00:00Z');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

function formatDate(iso: ISODateString): string {
  const d = new Date(iso + 'T00:00:00Z');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function statusBadgeClass(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.Submitted:       return 'submitted';
    case TimesheetStatus.ManagerApproved: return 'approved';
    case TimesheetStatus.Rejected:        return 'rejected';
    default:                              return 'pending';
  }
}

function statusBadgeLabel(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.Submitted:       return 'Pending';
    case TimesheetStatus.ManagerApproved: return 'Approved';
    case TimesheetStatus.Rejected:        return 'Rejected';
    default:                              return status;
  }
}

function weekColor(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.ManagerApproved: return 'var(--green, #22c55e)';
    case TimesheetStatus.Submitted:       return 'var(--accent)';
    case TimesheetStatus.Rejected:        return 'var(--red, #ef4444)';
    default:                              return 'var(--text-dim)';
  }
}

function weekBg(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.ManagerApproved: return 'rgba(34,197,94,0.12)';
    case TimesheetStatus.Submitted:       return 'rgba(99,102,241,0.1)';
    case TimesheetStatus.Rejected:        return 'rgba(239,68,68,0.1)';
    default:                              return 'var(--surface2)';
  }
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── WeekChip ─────────────────────────────────────────────────────────────────

function WeekChip({ week, onClick }: { week: WeekSummary; onClick: () => void }) {
  const color = weekColor(week.status);
  const bg = weekBg(week.status);

  return (
    <div
      onClick={onClick}
      title="Click to view week detail"
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        flex: 1,
        minWidth: 110,
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '10px 12px',
        background: bg,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s',
      }}
      onMouseOver={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
        el.style.borderColor = 'var(--accent)';
        el.style.transform = 'translateY(-1px)';
      }}
      onMouseOut={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.boxShadow = '';
        el.style.borderColor = '';
        el.style.transform = '';
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {week.weekNumber}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{week.label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginTop: 6 }}>
        {fmtHours(week.totalHours + week.bhHours)}
      </div>
      <div style={{ fontSize: 10.5, color, marginTop: 2, textTransform: 'capitalize' }}>
        {statusBadgeLabel(week.status).toLowerCase()}
      </div>
      <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 6, fontWeight: 600, letterSpacing: '0.03em' }}>
        View →
      </div>
    </div>
  );
}

// ─── ApprovalMonthCard ────────────────────────────────────────────────────────

function BackupApproverIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 10, height: 10, strokeWidth: 2.5 }}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ApprovalMonthCard({
  approval,
  isActioning,
  onApprove,
  onReject,
  onDrillDown,
}: {
  approval: PendingApprovalWithWeeks;
  isActioning: boolean;
  onApprove: () => void;
  onReject: () => void;
  onDrillDown: (week: WeekSummary) => void;
}) {
  const isActionable = approval.status === TimesheetStatus.Submitted;
  const monthLabel = formatPeriodMonthLabel(approval.periodMonth);

  const rdHours    = approval.breakdownByType[ProjectType.RD]    ?? 0;
  const gaHours    = approval.breakdownByType[ProjectType.GA]    ?? 0;
  const leaveHours = approval.breakdownByType[ProjectType.Leave] ?? 0;
  const rdPct = approval.totalHours > 0 ? Math.round(rdHours / approval.totalHours * 100) : 0;
  const totalWithBH = approval.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0);

  return (
    <div className="ts-card" style={{ marginBottom: 18, opacity: isActionable ? 1 : 0.65 }}>
      {/* Header */}
      <div className="ts-card-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="av" style={{ width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
            {getInitials(approval.personName)}
          </div>
          <div>
            <div className="ts-card-title">
              {approval.personName}
              {approval.source === 'asset' && (
                <span
                  className="badge"
                  style={{
                    marginLeft: 8,
                    background: 'rgba(99,102,241,0.15)',
                    color: 'var(--accent)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  Asset · {approval.assetCode || '—'}
                </span>
              )}
              {approval.isBackupApproval && (
                <span
                  className="badge"
                  style={{
                    marginLeft: 8,
                    background: 'rgba(234,179,8,0.15)',
                    color: '#b45309',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <BackupApproverIcon /> Backup Approver
                </span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              {approval.department}&nbsp;·&nbsp;{monthLabel}
            </div>
          </div>
        </div>
        <span className={`app-badge ${statusBadgeClass(approval.status)}`}>
          {statusBadgeLabel(approval.status)}
        </span>
      </div>

      {/* Approved banner */}
      {approval.status === TimesheetStatus.ManagerApproved && approval.managerApprovedDate && (
        <div className="info-box green" style={{ margin: '12px 18px 0' }}>
          <CheckIcon />
          &nbsp;Month approved on {formatDate(approval.managerApprovedDate)}
        </div>
      )}

      {/* Rejected banner */}
      {approval.status === TimesheetStatus.Rejected && (
        <div className="info-box red" style={{ margin: '12px 18px 0' }}>
          <XIcon />
          &nbsp;Month rejected — employee has been notified
        </div>
      )}

      {/* Backup approver notice */}
      {approval.isBackupApproval && approval.status === TimesheetStatus.Submitted && (
        <div
          className="info-box"
          style={{
            margin: '12px 18px 0',
            background: 'rgba(234,179,8,0.08)',
            border: '1px solid rgba(234,179,8,0.3)',
            color: '#92400e',
          }}
        >
          <BackupApproverIcon />
          &nbsp;You are acting as backup approver — the primary manager is unavailable
        </div>
      )}

      {/* Weeks + breakdown */}
      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {approval.weeks.map(week => (
            <WeekChip key={week.weekNumber} week={week} onClick={() => onDrillDown(week)} />
          ))}
        </div>

        <div
          style={{
            display: 'flex', gap: 16, flexWrap: 'wrap',
            marginTop: 14, paddingTop: 14,
            borderTop: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Projects&nbsp;
            <strong style={{ color: 'var(--text)' }}>{fmtHours(rdHours + gaHours)}</strong>
            &nbsp;<span style={{ color: 'var(--accent)' }}>({rdPct}% R&amp;D)</span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Leave&nbsp;<strong style={{ color: 'var(--text)' }}>{fmtHours(leaveHours)}</strong>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            Running total&nbsp;<strong style={{ color: 'var(--text)' }}>{fmtHours(totalWithBH)}</strong>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="ts-actions">
        <div className="ts-actions-l">
          <span className="saved-lbl">{monthLabel}</span>
        </div>
        <div className="ts-actions-r">
          {isActionable ? (
            <>
              <button className="btn btn-danger" onClick={onReject} disabled={isActioning}>
                <XIcon />&nbsp;Reject Month
              </button>
              <button className="btn btn-success" onClick={onApprove} disabled={isActioning}>
                <CheckIcon />&nbsp;Approve Month
              </button>
            </>
          ) : (
            <span className="saved-lbl">
              {approval.status === TimesheetStatus.ManagerApproved ? 'Month Approved' : 'Month Rejected'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── WeekDrillDownModal ───────────────────────────────────────────────────────

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 11, height: 11, strokeWidth: 2.5 }}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function WeekDrillDownModal({
  approval,
  week,
  onClose,
  onSaved,
}: {
  approval: PendingApprovalWithWeeks;
  week: WeekSummary;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const connector = useConnector();
  const days = getWeekDays(week.weekStart);

  // ── Bank holiday indication ──────────────────────────────────────────────────
  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);
  useEffect(() => {
    const year = parseInt(week.weekStart.slice(0, 4), 10);
    void connector.getBankHolidays(year).then(setBankHolidays);
  }, [connector, week.weekStart]);

  // Deduplicate by date — SP can have UK + All rows for the same calendar date
  const bhInWeekMap = new Map<ISODateString, BankHoliday>();
  for (const bh of bankHolidays) {
    if (!bhInWeekMap.has(bh.holidayDate)) bhInWeekMap.set(bh.holidayDate, bh);
  }
  // BH days that fall within this specific week
  const bhDaysInWeek = days.filter(d => bhInWeekMap.has(d));
  // Days where the person entered project hours on a BH (chose to work)
  const daysWithProjectHours = new Set(
    week.entries
      .filter(e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.hours > 0)
      .map(e => e.dateWorked)
  );

  // Exclude the NON-WORKING sentinel from the project grid (defensive)
  const projectCodes = [...new Set(
    week.entries
      .filter(e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE)
      .map(e => e.projectCode)
  )];

  const [editedHours, setEditedHours] = useState<Map<string, number>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isEditable = approval.status === TimesheetStatus.Submitted;
  const hasChanges = editedHours.size > 0;
  const listName = approval.source === 'asset' ? SP_LISTS.ASSET_TIMESHEETS : SP_LISTS.TIME_ENTRIES;

  const hoursGrid = projectCodes.map(code => {
    const dayData = days.map(day => {
      const cellEntries = week.entries.filter(e => e.projectCode === code && e.dateWorked === day);
      const singleEntry = cellEntries.length === 1 ? cellEntries[0] : null;
      const canEdit = isEditable && singleEntry !== null;
      const effectiveHours = singleEntry && editedHours.has(singleEntry.id)
        ? editedHours.get(singleEntry.id)!
        : cellEntries.reduce((s, e) => s + e.hours, 0);
      return { singleEntry, canEdit, effectiveHours };
    });
    const rowTotal = dayData.reduce((s, d) => s + d.effectiveHours, 0);
    return { projectCode: code, dayData, rowTotal };
  });

  const dailyTotals = days.map((day, i) => {
    const workHours = hoursGrid.reduce((s, row) => s + row.dayData[i].effectiveHours, 0);
    // A Mon-Sun week can straddle two calendar months — only count a BH day here if
    // it actually falls within this approval's period month, so a boundary day (e.g.
    // 31 Aug in a "W/C 31 Aug" week belonging to September) isn't double-attributed.
    const bhHours = (bhInWeekMap.has(day) && !daysWithProjectHours.has(day) && day.slice(0, 7) === approval.periodMonth)
      ? VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS
      : 0;
    return workHours + bhHours;
  });
  const grandTotal = dailyTotals.reduce((s, t) => s + t, 0);

  const handleCellChange = (entryId: string, raw: string) => {
    setSaveError(null);
    setEditedHours(prev => {
      const next = new Map(prev);
      const val = parseFloat(raw);
      if (!isNaN(val) && val >= 0) next.set(entryId, val);
      else next.delete(entryId);
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await Promise.all(
        [...editedHours.entries()].map(([id, hours]) =>
          connector.updateTimeEntry(id, { hours }, listName)
        )
      );
      setEditedHours(new Map());
      onSaved?.();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed. Please try again.');
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges && !isSaving) {
      if (!window.confirm('You have unsaved changes. Discard them?')) return;
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          width: '100%', maxWidth: 860, maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        }}
      >
        {/* Modal header */}
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '18px 20px', borderBottom: '1px solid var(--border)',
            position: 'sticky', top: 0, background: 'var(--surface)',
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              {approval.personName} — {week.weekNumber} Detail
              {isEditable && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 10.5, fontWeight: 600, color: 'var(--accent)',
                  background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
                  borderRadius: 5, padding: '2px 7px', letterSpacing: '0.04em',
                }}>
                  <EditIcon /> Editable
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{week.label}</div>
          </div>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 20, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Timesheet grid */}
        <div style={{ padding: 20, overflowX: 'auto' }}>
          {isEditable && (
            <div style={{
              fontSize: 11.5, color: 'var(--text-muted)',
              marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <EditIcon />
              Click any hour cell to edit. Changes are saved when you click &ldquo;Save Changes&rdquo;.
            </div>
          )}
          <table className="ts-tbl">
            <thead>
              <tr>
                <th className="tcol">Project / Task</th>
                {DAY_NAMES.map((d, i) => {
                  const isBH = bhInWeekMap.has(days[i]);
                  return (
                    <th key={d} style={isBH ? { color: 'var(--amber, #d97706)' } : undefined}>
                      {d}
                      {isBH && (
                        <span
                          title={bhInWeekMap.get(days[i])!.holidayName}
                          style={{
                            display: 'inline-block',
                            width: 5, height: 5,
                            borderRadius: '50%',
                            background: 'var(--amber, #d97706)',
                            marginLeft: 3,
                            verticalAlign: 'middle',
                            marginBottom: 1,
                          }}
                        />
                      )}
                      <br />
                      <small style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>
                        {formatShortDate(days[i])}
                      </small>
                    </th>
                  );
                })}
                <th className="ttotal">Total</th>
              </tr>
            </thead>
            <tbody>
              {hoursGrid.map(row => (
                <tr key={row.projectCode}>
                  <td className="tcell">
                    <div className="task-name">{row.projectCode}</div>
                  </td>
                  {row.dayData.map((cell, i) => {
                    const isDirty = cell.singleEntry && editedHours.has(cell.singleEntry.id);
                    return (
                      <td key={i}>
                        {cell.canEdit ? (
                          <input
                            className={`hr-input${cell.effectiveHours === 0 ? ' empty' : ''}${isDirty ? ' modified' : ''}`}
                            type="number"
                            min={0}
                            step={0.5}
                            value={cell.effectiveHours === 0 ? '' : cell.effectiveHours}
                            placeholder="–"
                            onChange={e => handleCellChange(cell.singleEntry!.id, e.target.value)}
                            style={isDirty ? { borderColor: 'var(--accent)', background: 'rgba(99,102,241,0.06)' } : undefined}
                          />
                        ) : (
                          <input
                            className="hr-input-ro"
                            readOnly
                            value={cell.effectiveHours > 0 ? cell.effectiveHours : ''}
                            placeholder="–"
                            type="number"
                          />
                        )}
                      </td>
                    );
                  })}
                  <td className="total-cell">
                    {row.rowTotal > 0 ? fmtHours(row.rowTotal) : '–'}
                  </td>
                </tr>
              ))}
              {bhDaysInWeek.length > 0 && (
                <tr style={{ background: 'rgba(217,119,6,0.05)' }}>
                  <td className="tcell">
                    <div className="task-name" style={{ color: 'var(--amber, #d97706)', fontWeight: 600 }}>
                      🏖 Bank Holiday
                    </div>
                  </td>
                  {days.map((day, i) => {
                    const bh = bhInWeekMap.get(day);
                    if (!bh) {
                      return (
                        <td key={i} style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>–</td>
                      );
                    }
                    const worked = daysWithProjectHours.has(day);
                    return (
                      <td key={i} style={{ textAlign: 'center' }}>
                        <span
                          title={bh.holidayName}
                          style={worked
                            ? { color: 'var(--amber, #d97706)', textDecoration: 'line-through', opacity: 0.65, fontSize: 12 }
                            : { color: 'var(--amber, #d97706)', fontWeight: 700, fontSize: 12 }
                          }
                        >
                          {VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS}
                        </span>
                      </td>
                    );
                  })}
                  <td className="total-cell" style={{ color: 'var(--amber, #d97706)' }}>
                    {(() => {
                      const countedDays = bhDaysInWeek.filter(d => !daysWithProjectHours.has(d)).length;
                      return countedDays > 0 ? fmtHours(countedDays * VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS) : '–';
                    })()}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="tfoot-row">
                <td className="tfoot-lbl">Daily Total</td>
                {dailyTotals.map((t, i) => (
                  <td key={i} className="tfoot-day">{t > 0 ? fmtHours(t) : '–'}</td>
                ))}
                <td className="tfoot-grand">{fmtHours(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {saveError && (
          <div className="err-banner" style={{ margin: '0 20px 12px' }}>{saveError}</div>
        )}

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={handleClose} disabled={isSaving}>Close</button>
          {isEditable && (
            <button
              className="btn btn-success"
              onClick={() => void handleSave()}
              disabled={!hasChanges || isSaving}
              style={{ minWidth: 130 }}
            >
              {isSaving ? (
                <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Saving…</>
              ) : (
                <><CheckIcon />&nbsp;Save Changes{hasChanges ? ` (${editedHours.size})` : ''}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ConfirmBackdrop ──────────────────────────────────────────────────────────

function ConfirmBackdrop({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10,12,20,0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'fadeIn 0.15s ease',
      }}
    >
      {children}
    </div>
  );
}

// ─── RejectModal ──────────────────────────────────────────────────────────────

function RejectModal({
  approval,
  note,
  error,
  isActioning,
  onNoteChange,
  onConfirm,
  onClose,
}: {
  approval: PendingApprovalWithWeeks;
  note: string;
  error: string;
  isActioning: boolean;
  onNoteChange: (v: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmBackdrop>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          width: '100%', maxWidth: 460,
          boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'slideUp 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Coloured top bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #dc2626, #ef4444)' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Icon circle */}
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(220,38,38,0.10)',
              border: '1.5px solid rgba(220,38,38,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" style={{ width: 18, height: 18, strokeWidth: 2.5 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Reject Month
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                This action will notify the employee
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', fontSize: 18, lineHeight: 1,
              padding: '2px 4px', borderRadius: 4,
              transition: 'color 0.12s',
            }}
            onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; }}
            onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-dim)'; }}
          >
            ✕
          </button>
        </div>

        {/* Person info pill */}
        <div style={{ margin: '16px 20px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px',
          }}>
            <div className="av" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
              {getInitials(approval.personName)}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{approval.personName}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
                {approval.department}&nbsp;·&nbsp;
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatPeriodMonthLabel(approval.periodMonth)}</span>
                &nbsp;·&nbsp;{fmtHours(approval.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0))} total
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
              Rejection reason&nbsp;<span style={{ color: 'var(--red)', fontWeight: 700 }}>*</span>
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>(min. 10 characters)</span>
            </label>
            <span style={{ fontSize: 11, color: note.trim().length >= 10 ? 'var(--text-muted)' : 'var(--red)', flexShrink: 0 }}>
              {note.trim().length}/10
            </span>
          </div>
          <textarea
            className="fnote"
            value={note}
            onChange={e => onNoteChange(e.target.value)}
            placeholder="Explain why this timesheet is being rejected…"
            style={{ resize: 'vertical', minHeight: 90 }}
          />
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: 'var(--red)', marginTop: 6,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '16px 20px 20px',
          borderTop: '1px solid var(--border)',
          marginTop: 16,
        }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={isActioning}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isActioning || note.trim().length < 10}
            style={{ minWidth: 130 }}
          >
            {isActioning ? (
              <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Rejecting…</>
            ) : (
              <><XIcon />&nbsp;Confirm Rejection</>
            )}
          </button>
        </div>
      </div>
    </ConfirmBackdrop>
  );
}

// ─── ApproveModal ─────────────────────────────────────────────────────────────

function ApproveModal({
  approval,
  isActioning,
  onConfirm,
  onClose,
}: {
  approval: PendingApprovalWithWeeks;
  isActioning: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmBackdrop>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          width: '100%', maxWidth: 420,
          boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'slideUp 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Coloured top bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #16a34a, #22c55e)' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Icon circle */}
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(22,163,74,0.10)',
              border: '1.5px solid rgba(22,163,74,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" style={{ width: 18, height: 18, strokeWidth: 2.5 }}>
                <circle cx="12" cy="12" r="10" />
                <polyline points="8 12 11 15 16 9" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Approve Month
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                Employee will be notified of approval
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', fontSize: 18, lineHeight: 1,
              padding: '2px 4px', borderRadius: 4,
              transition: 'color 0.12s',
            }}
            onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; }}
            onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-dim)'; }}
          >
            ✕
          </button>
        </div>

        {/* Person info pill */}
        <div style={{ margin: '16px 20px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px',
          }}>
            <div className="av" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
              {getInitials(approval.personName)}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{approval.personName}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
                {approval.department}&nbsp;·&nbsp;
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatPeriodMonthLabel(approval.periodMonth)}</span>
                &nbsp;·&nbsp;{fmtHours(approval.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0))} total
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation message */}
        <div style={{ padding: '14px 20px 0' }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
            You&apos;re about to approve <strong style={{ color: 'var(--text)' }}>{formatPeriodMonthLabel(approval.periodMonth)}</strong> for{' '}
            <strong style={{ color: 'var(--text)' }}>{approval.personName}</strong>.
            This will lock the timesheet and mark it as complete.
          </p>
          {approval.isBackupApproval && (
            <p style={{
              fontSize: 12, color: '#92400e', lineHeight: 1.5, margin: '10px 0 0',
              padding: '8px 10px',
              background: 'rgba(234,179,8,0.08)',
              border: '1px solid rgba(234,179,8,0.3)',
              borderRadius: 6,
            }}>
              You are approving as <strong>Backup Approver</strong>. The employee&apos;s primary manager was unavailable.
            </p>
          )}
        </div>

        {/* Hours summary chips */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 20px 0', flexWrap: 'wrap' }}>
          {[
            { label: 'Total hours', value: fmtHours(approval.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0)), color: 'var(--accent)' },
            { label: 'Weeks', value: `${approval.weeks.length}`, color: 'var(--green)' },
          ].map(chip => (
            <div key={chip.label} style={{
              flex: 1, minWidth: 100,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: chip.color }}>{chip.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{chip.label}</div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '16px 20px 20px',
          borderTop: '1px solid var(--border)',
          marginTop: 16,
        }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={isActioning}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={onConfirm}
            disabled={isActioning}
            style={{ minWidth: 130 }}
          >
            {isActioning ? (
              <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Approving…</>
            ) : (
              <><CheckIcon />&nbsp;Yes, Approve</>
            )}
          </button>
        </div>
      </div>
    </ConfirmBackdrop>
  );
}

// ─── ApprovalDashboard ────────────────────────────────────────────────────────

export interface ApprovalDashboardProps {
  managerEmail: string;
  managerName: string;
  approvals: UseManagerApprovalsResult;
}

export function ApprovalDashboard({ managerEmail, managerName, approvals }: ApprovalDashboardProps) {
  const [filter, setFilter] = useState<'pending' | 'done'>('pending');
  const [drillDown, setDrillDown] = useState<{ approval: PendingApprovalWithWeeks; week: WeekSummary } | null>(null);
  const [approveTarget, setApproveTarget] = useState<PendingApprovalWithWeeks | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingApprovalWithWeeks | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectError, setRejectError] = useState('');

  const {
    pendingItems,
    reviewedItems,
    isLoading,
    isReviewedLoading,
    loadError,
    isActioning,
    actionError,
    approveMonth,
    rejectMonth,
    loadReviewed,
    refresh,
  } = approvals;

  const handleFilterChange = (f: 'pending' | 'done') => {
    setFilter(f);
    if (f === 'done') void loadReviewed();
  };

  const openApprove = (approval: PendingApprovalWithWeeks) => {
    setApproveTarget(approval);
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    try {
      await approveMonth(approveTarget, managerName, managerEmail);
      setApproveTarget(null);
    } catch {
      setApproveTarget(null);
    }
  };

  const openReject = (approval: PendingApprovalWithWeeks) => {
    setRejectTarget(approval);
    setRejectNote('');
    setRejectError('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setRejectError('');
    try {
      await rejectMonth(rejectTarget, rejectNote);
      setRejectTarget(null);
      setRejectNote('');
    } catch (e) {
      setRejectError(e instanceof Error ? e.message : 'Rejection failed.');
    }
  };

  const displayItems = filter === 'pending' ? pendingItems : reviewedItems;
  const isDisplayLoading = filter === 'pending' ? isLoading : isReviewedLoading;

  const primaryItems = displayItems.filter(a => !a.isBackupApproval);
  const backupItems = displayItems.filter(a => a.isBackupApproval);

  const employeeItems = primaryItems.filter(a => a.source === 'employee');
  const assetItems = primaryItems.filter(a => a.source === 'asset');
  const backupEmployeeItems = backupItems.filter(a => a.source === 'employee');
  const backupAssetItems = backupItems.filter(a => a.source === 'asset');

  const renderCard = (approval: PendingApprovalWithWeeks) => (
    <ApprovalMonthCard
      key={`${approval.source}::${approval.personId}::${approval.periodMonth}::${approval.assetCode}`}
      approval={approval}
      isActioning={isActioning}
      onApprove={() => openApprove(approval)}
      onReject={() => openReject(approval)}
      onDrillDown={week => setDrillDown({ approval, week })}
    />
  );

  const SectionHeader = ({ title, count }: { title: string; count: number }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 2px 10px', paddingTop: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.02em' }}>
        {title}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
        {count} {count === 1 ? 'item' : 'items'}
      </div>
    </div>
  );

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Timesheets</span>
            <span className="sep">/</span>
            <span className="cur">My Approvals</span>
          </div>
        </div>
        <div className="topbar-r">
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className={`filter-tab${filter === 'pending' ? ' active' : ''}`}
              onClick={() => handleFilterChange('pending')}
            >
              Needs Action&nbsp;<span>({pendingItems.length})</span>
            </button>
            <button
              className={`filter-tab${filter === 'done' ? ' active' : ''}`}
              onClick={() => handleFilterChange('done')}
            >
              Reviewed
            </button>
          </div>
        </div>
      </div>

      {/* Cards area */}
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', padding: '20px 28px' }}>
        {loadError && (
          <div className="err-banner" style={{ marginBottom: 16 }}>{loadError.message}</div>
        )}
        {actionError && (
          <div className="err-banner" style={{ marginBottom: 16 }}>{actionError.message}</div>
        )}

        {isDisplayLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
            <div className="spinner" aria-label="Loading" />
          </div>
        ) : displayItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dim)', fontSize: 13 }}>
            {filter === 'pending' ? 'No pending approvals' : 'No reviewed timesheets'}
          </div>
        ) : (
          <>
            {employeeItems.length > 0 && (
              <>
                <SectionHeader title="Employee Timesheets" count={employeeItems.length} />
                {employeeItems.map(renderCard)}
              </>
            )}
            {assetItems.length > 0 && (
              <>
                <div style={{ marginTop: employeeItems.length > 0 ? 8 : 0 }}>
                  <SectionHeader title="Asset Timesheets" count={assetItems.length} />
                </div>
                {assetItems.map(renderCard)}
              </>
            )}
            {(backupEmployeeItems.length > 0 || backupAssetItems.length > 0) && (
              <>
                <div
                  style={{
                    marginTop: (employeeItems.length > 0 || assetItems.length > 0) ? 20 : 0,
                    marginBottom: 10,
                    paddingTop: (employeeItems.length > 0 || assetItems.length > 0) ? 16 : 0,
                    borderTop: (employeeItems.length > 0 || assetItems.length > 0) ? '1px dashed var(--border)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <BackupApproverIcon />
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Backup Approver Queue
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      — covering for primary manager
                    </span>
                  </div>
                </div>
                {backupEmployeeItems.length > 0 && (
                  <>
                    <SectionHeader title="Employee Timesheets" count={backupEmployeeItems.length} />
                    {backupEmployeeItems.map(renderCard)}
                  </>
                )}
                {backupAssetItems.length > 0 && (
                  <>
                    <div style={{ marginTop: backupEmployeeItems.length > 0 ? 8 : 0 }}>
                      <SectionHeader title="Asset Timesheets" count={backupAssetItems.length} />
                    </div>
                    {backupAssetItems.map(renderCard)}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Approve confirmation modal */}
      {approveTarget && (
        <ApproveModal
          approval={approveTarget}
          isActioning={isActioning}
          onConfirm={() => void handleApproveConfirm()}
          onClose={() => setApproveTarget(null)}
        />
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          approval={rejectTarget}
          note={rejectNote}
          error={rejectError}
          isActioning={isActioning}
          onNoteChange={setRejectNote}
          onConfirm={() => void handleRejectConfirm()}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {/* Week drill-down modal */}
      {drillDown && (
        <WeekDrillDownModal
          approval={drillDown.approval}
          week={drillDown.week}
          onClose={() => setDrillDown(null)}
          onSaved={() => { setDrillDown(null); void refresh(); }}
        />
      )}
    </>
  );
}
